/**
 * Payroll API Client
 * Epic 112: Bérszámfejtés Internalizáció — PAY-7
 *
 * Endpoint base: /api/v1/payroll/
 */

import { api } from '@/api/client';

// ============================================
// TYPES
// ============================================

export type PayrollRunStatus =
  | 'DRAFT'
  | 'CALCULATED'
  | 'BOLT_APPROVED'
  | 'CENTRAL_REVIEWED'
  | 'FINALIZED';

export type PayrollPeriodType = 'MONTHLY' | 'CORRECTION';

export interface PayrollRunDto {
  id: string;
  tenantId: string;
  year: number;
  month: number;
  periodType: PayrollPeriodType;
  status: PayrollRunStatus;
  isKivaRun: boolean;
  totalGross: number;
  totalNet: number;
  totalEmployerCost: number;
  employeeCount: number;
  boltApprovedBy?: string | undefined;
  boltApprovedAt?: string | undefined;
  centralReviewedBy?: string | undefined;
  centralReviewedAt?: string | undefined;
  finalizedBy?: string | undefined;
  finalizedAt?: string | undefined;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PayrollRunListResponse {
  data: PayrollRunDto[];
}

export interface PayrollRunResponse {
  data: PayrollRunDto;
}

export interface CreatePayrollRunInput {
  year: number;
  month: number;
  periodType?: PayrollPeriodType | undefined;
  isKivaRun: boolean;
}

export interface ListPayrollRunsFilter {
  year?: number | undefined;
  status?: PayrollRunStatus | undefined;
}

// ============================================
// API FUNCTIONS
// ============================================

export async function listRuns(filter?: ListPayrollRunsFilter): Promise<PayrollRunListResponse> {
  const params = new URLSearchParams();
  if (filter?.year !== undefined) params.set('year', String(filter.year));
  if (filter?.status !== undefined) params.set('status', filter.status);
  const qs = params.toString();
  return api.get<PayrollRunListResponse>(`/payroll/runs${qs ? `?${qs}` : ''}`);
}

export async function getRun(id: string): Promise<PayrollRunResponse> {
  return api.get<PayrollRunResponse>(`/payroll/runs/${id}`);
}

export async function createDraft(input: CreatePayrollRunInput): Promise<PayrollRunResponse> {
  return api.post<PayrollRunResponse>('/payroll/runs', input);
}

export async function calculate(id: string): Promise<PayrollRunResponse> {
  return api.post<PayrollRunResponse>(`/payroll/runs/${id}/calculate`, {});
}

export async function boltApprove(id: string): Promise<PayrollRunResponse> {
  return api.post<PayrollRunResponse>(`/payroll/runs/${id}/bolt-approve`, {});
}

export async function centralReview(id: string): Promise<PayrollRunResponse> {
  return api.post<PayrollRunResponse>(`/payroll/runs/${id}/central-review`, {});
}

export async function finalize(id: string): Promise<PayrollRunResponse> {
  return api.post<PayrollRunResponse>(`/payroll/runs/${id}/finalize`, {});
}

/** Returns the download URL for CSV or Excel export (only for FINALIZED runs). */
export function exportUrl(id: string, format: 'csv' | 'xlsx'): string {
  return `/api/v1/payroll/runs/${id}/export/${format}`;
}
